import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlashIcon, CaretDownIcon } from "assets/website";
import { DropdownMenu } from "components/dropdown-menu";
import { CreditSlider } from "components/credit-slider";
import { CreditAmountField } from "components/credit-slider/credit-amount-field";
import { useCreditPricing } from "hooks/useCreditPricing";
import {
  buildCreditSalesContactUrl,
  CREDIT_CURRENCIES,
  CREDIT_PURCHASE_STEP,
  CURRENCY_SYMBOLS,
  MIN_CREDIT_PURCHASE,
  formatCurrency,
  type CreditCurrency,
} from "utils/pricing";

interface CreditsCalculatorProps {
  onGetStarted: (creditsSelected: number) => void;
}

/**
 * Interactive credit purchase estimate on the public pricing page. Purely
 * illustrative — the actual charge is created from the same flat rate by the
 * in-app top-up flow once the user has an account.
 */
const CreditsCalculator = ({ onGetStarted }: CreditsCalculatorProps) => {
  const [credits, setCredits] = useState(MIN_CREDIT_PURCHASE);
  const [currency, setCurrency] = useState<CreditCurrency>("NGN");
  const [draftCredits, setDraftCredits] = useState<number | null>(null);
  const navigate = useNavigate();
  const { pricing, priceFor } = useCreditPricing();

  const price = priceFor(credits, currency);
  const isSalesRequest = draftCredits !== null && draftCredits > pricing.maxPurchase;

  const handlePrimaryAction = () => {
    if (isSalesRequest) {
      navigate(buildCreditSalesContactUrl(draftCredits ?? undefined));
      return;
    }

    onGetStarted(credits);
  };

  return (
    <div className="bg-white rounded-[15px] border border-[#98A2B3] p-6 sm:p-10 lg:p-11 max-w-[962px] mx-auto">
      {/* Badge */}
      <div className="flex justify-center mb-4">
        <span className="inline-flex items-center gap-2 h-8 px-2.5 py-1 bg-[#E6FFE3] text-[#004600] text-base rounded-[14px]">
          <FlashIcon className="w-5 h-5" aria-hidden="true" />
          Credits Calculator
        </span>
      </div>

      <p className="text-center text-[#475467] text-lg sm:text-2xl leading-[1.3] tracking-[-0.24px] mb-10 sm:mb-16 max-w-[874px] mx-auto">
        Every registered user receives free {pricing.weeklyCredits} credits every week. Top up whenever you need
        additional AI evaluations.
      </p>

      <CreditSlider
        min={pricing.minPurchase}
        max={pricing.maxPurchase}
        step={CREDIT_PURCHASE_STEP}
        value={credits}
        onChange={setCredits}
        label="Credits to purchase"
        className="mb-10"
      />

      {isSalesRequest && (
        <div className="mb-6 rounded-xl border border-[#B2CCFF] bg-[#EFF8FF] px-4 py-3 text-sm text-[#344054]">
          Need more than {pricing.maxPurchase.toLocaleString()} credits? We&apos;ll route you to sales.
        </div>
      )}

      {/* Currency + read-out */}
      <div className="grid gap-4 sm:grid-cols-[auto_1fr_1fr] items-end">
        <DropdownMenu
          align="left"
          options={CREDIT_CURRENCIES.map((code) => ({
            key: code,
            text: `${code} ${CURRENCY_SYMBOLS[code]}`,
            active: code === currency,
            onClick: () => setCurrency(code),
          }))}
          trigger={
            <button
              type="button"
              aria-label={`Currency: ${currency}`}
              className="flex h-[50px] w-full sm:w-auto items-center justify-center gap-2.5 rounded-[6px] border-[1.5px] border-[#000B55] bg-white px-3 font-medium text-[#000B55]"
            >
              {currency} {CURRENCY_SYMBOLS[currency]}
              <CaretDownIcon className="w-[18px] h-[18px]" aria-hidden="true" />
            </button>
          }
        />

        <div>
          <label htmlFor="credit-amount" className="block text-base text-[#1D2939] mb-1.5">
            Credits
          </label>
          <CreditAmountField
            id="credit-amount"
            value={credits}
            onChange={setCredits}
            onDraftChange={setDraftCredits}
          />
        </div>

        <div>
          <label htmlFor="credit-price" className="block text-base text-[#1D2939] mb-1.5">
            Price
          </label>
          <output
            id="credit-price"
            className="flex items-center h-[50px] pl-2.5 pr-4 border border-[rgba(1,33,105,0.5)] rounded-[8px] text-[#101828] bg-white font-semibold"
          >
            {formatCurrency(price, currency)}
          </output>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <button
          type="button"
          onClick={handlePrimaryAction}
          className="h-12 px-8 bg-[#012169] text-white font-medium rounded-[8px] shadow-[0px_4px_8px_0px_rgba(1,33,105,0.25)] hover:bg-[#011557] transition-colors"
        >
          {isSalesRequest ? "Talk to sales" : "Get Started"}
        </button>
      </div>
    </div>
  );
};

export { CreditsCalculator };
